import {PersonalizedDiscountToken} from '@aboutyou/grpc-clients/tadarida/aysa_api/features/discount/v1/discount.types';
import {
  GetDiscountsResponse,
  GetDiscountsResponse_Discount,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/campaigns/v1/service.types';
import {
  useLocalStorage,
  useLocalStorageValueCallback,
  useSessionStorage,
} from '@aboutyou/storage';
import React, {
  FC,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {useDiscounts, useDiscountsCallback} from '../hooks/useDiscounts';

type PersistedDiscountValue = {
  // Only if the user did not take any action so far, the discount id will be `null`.
  id: string | null;
  // in case the id is set, and `disabled` is set to true, this means the user explicitly decided to not select the recommended personal discount. We store the id and the selection so that if a new discount appears, it can be selected
  disabled: boolean;
};

const PERSISTED_SELECTED_DISCOUNT_STORAGE_KEY = 'ay_selected_discount';
const usePersistedSelectedDiscount = () => {
  return useLocalStorage<PersistedDiscountValue>({
    key: PERSISTED_SELECTED_DISCOUNT_STORAGE_KEY,
    defaultValue: {id: null, disabled: false},
  });
};

const usePersistedSelectedDiscountCallback = () => {
  const getPersistedSelectedDiscount =
    useLocalStorageValueCallback<PersistedDiscountValue>({
      key: PERSISTED_SELECTED_DISCOUNT_STORAGE_KEY,
      defaultValue: {id: null, disabled: false},
    });

  return () => {
    const persistedDiscount = getPersistedSelectedDiscount();
    return persistedDiscount || {id: null, disabled: false};
  };
};

const CAMPAIGN_KEY_SNAPSHOT_STORAGE_KEY = 'ay_co_discount';
const useSnapshotCampaignKey = () => {
  return useSessionStorage<
    | {
        campaignKey: string | undefined;
        selectedDiscount?: PersonalizedDiscountToken;
      }
    | undefined
  >({
    key: CAMPAIGN_KEY_SNAPSHOT_STORAGE_KEY,
    defaultValue: undefined,
    forcePersistValue_ILLEGALLY: true,
  });
};

export const useCreateSnapshotCampaign = () => {
  const [, update] = useSnapshotCampaignKey();
  return update;
};

export const useRequestSnapshotCampaignKey = () => {
  const [value] = useSnapshotCampaignKey();
  return value;
};

export type SelectedDiscountValue = GetDiscountsResponse_Discount | null;

const UserSelectedDiscountContext = createContext<{
  selectedDiscount: SelectedDiscountValue;
  updateSelectedDiscount: (selectedDiscount: PersistedDiscountValue) => void;
}>({
  selectedDiscount: null,
  updateSelectedDiscount: () => {},
});

export const useSelectedDiscount = (): SelectedDiscountValue => {
  return useContext(UserSelectedDiscountContext).selectedDiscount;
};

export const useSelectedDiscountCallback = () => {
  const fetchDiscounts = useDiscountsCallback();
  const getPersistedDiscount = usePersistedSelectedDiscountCallback();

  return useCallback(async () => {
    const discounts = await fetchDiscounts().catch(() => {
      return undefined;
    });
    const currentPersistedDiscounts = getPersistedDiscount();
    return getCurrentDiscount(discounts, currentPersistedDiscounts);
  }, [fetchDiscounts, getPersistedDiscount]);
};

export const useUpdateSelectedDiscount = () => {
  const {updateSelectedDiscount} = useContext(UserSelectedDiscountContext);
  return useCallback(
    (id: string) => {
      updateSelectedDiscount({id, disabled: false});
    },
    [updateSelectedDiscount],
  );
};

export const useResetSelectedDiscount = () => {
  const {updateSelectedDiscount} = useContext(UserSelectedDiscountContext);
  return useCallback(() => {
    updateSelectedDiscount({id: null, disabled: false});
  }, [updateSelectedDiscount]);
};

export const UserSelectedDiscountProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const {data} = useDiscounts();
  const [persistedDiscount, updatePersistedDiscount] =
    usePersistedSelectedDiscount();

  const currentDiscount = useMemo(
    () => getCurrentDiscount(data, persistedDiscount!),
    [data, persistedDiscount],
  );

  const value = useMemo(() => {
    return {
      selectedDiscount: currentDiscount,
      updateSelectedDiscount: updatePersistedDiscount,
    };
    // only consider the current discount id as the backend could update the instances of the object, but we do not expect the values to change if the ID stays stable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDiscount?.id, updatePersistedDiscount]);

  return (
    <UserSelectedDiscountContext.Provider value={value}>
      {children}
    </UserSelectedDiscountContext.Provider>
  );
};

export const useGetActiveDiscountFromDiscounts = (
  discounts: GetDiscountsResponse_Discount[],
) => {
  const [persistedDiscount] = usePersistedSelectedDiscount();

  return useMemo(
    () =>
      getCurrentDiscountFromDiscountsOrPersisted(discounts, persistedDiscount!),
    [discounts, persistedDiscount],
  );
};

const getCurrentDiscount = (
  requestData: GetDiscountsResponse | undefined,
  persistedDiscount: PersistedDiscountValue,
): SelectedDiscountValue => {
  // no discounts, no selection
  if (!requestData) {
    return null;
  }
  const {discounts} = requestData;

  return getCurrentDiscountFromDiscountsOrPersisted(
    discounts,
    persistedDiscount,
  );
};

const getCurrentDiscountFromDiscountsOrPersisted = (
  discounts: GetDiscountsResponse_Discount[],
  persistedDiscount: PersistedDiscountValue,
): SelectedDiscountValue => {
  if (!discounts.length) {
    return null;
  }

  // prefer selection if it was not disabled
  if (!persistedDiscount.disabled) {
    const userPreference = discounts.find(discount => {
      return discount.id === persistedDiscount.id;
    });

    if (userPreference) {
      return userPreference;
    }
  }

  // find recommendations, if they are not disabled
  const recommendation = discounts.find(discount => {
    return discount.isRecommendedDiscount;
  });

  if (recommendation) {
    if (!persistedDiscount.disabled) {
      return recommendation;
    }
    if (recommendation.id !== persistedDiscount.id) {
      return recommendation;
    }
  }

  // fallback to px campaign if recommendation is disabled
  const fallback = discounts.find(discount => {
    return discount.isFallback;
  });

  if (fallback) {
    return fallback;
  }

  // no fallback and recommendation is disabled, so nothing is selected
  return null;
};
